/**
 * Description Formatter - Float Layout
 * 
 * Handles various HTML structures from Shopify.
 */

class DescriptionFormatter {
  constructor(options = {}) {
    this.options = {
      containerSelectors: [
        '.metafields-collection-description__container .boxed-text',
        '.metafields-products-additional__container .boxed-text',
        '.metafields-product-description__container .boxed-text'
      ],
      alternateDirection: true,
      mobileBreakpoint: 750,
      ...options
    };

    this.containers = [];
  }

  init() {
    this.normalizeContainers();

    const selectors = Array.isArray(this.options.containerSelectors)
      ? this.options.containerSelectors
      : [this.options.containerSelectors];

    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(container => {
        this.containers.push(container);
        this.formatContainer(container);
      });
    });

    if (this.containers.length > 0) {
      this.setupResponsiveHandler();
    }
  }

  formatContainer(container) {
    try {
      this.cleanInlineStyles(container);
      this.unwrapExistingLayouts(container);
      this.unwrapImageWrappers(container);
      this.processContent(container);
    } catch (error) {
      console.error('[DescriptionFormatter] Error:', error);
    }
  }

  /**
   * Fix nested/duplicate .metafields-collection-description__container elements
   * and clean up nested .liquid divs in the metafield content
   */
  normalizeContainers() {
    const containers = document.querySelectorAll('.metafields-collection-description__container');

    containers.forEach(outer => {
      const innerContainers = outer.querySelectorAll('.metafields-collection-description__container');
      innerContainers.forEach(inner => {
        if (inner === outer) return;
        const parent = inner.parentNode;
        while (inner.firstChild) {
          parent.insertBefore(inner.firstChild, inner);
        }
        inner.remove();
      });
    });

    const liquidWrappers = document.querySelectorAll('.metafields-collection-description__container .liquid');
    liquidWrappers.forEach(el => {
      const parent = el.parentNode;
      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      el.remove();
    });
  }

  /**
   * Remove inline float and text-align styles
   */
  cleanInlineStyles(container) {
    const images = container.querySelectorAll('img');
    images.forEach(img => {
      img.style.float = '';
      img.style.textAlign = '';
      img.style.margin = '';
      img.style.padding = '';
    });

    const styledDivs = container.querySelectorAll('div[style]');
    styledDivs.forEach(div => {
      div.removeAttribute('style');
    });
  }

  /**
   * Remove existing layout wrappers
   */
  unwrapExistingLayouts(container) {
    const layoutSelectors = [
      '.description-flex-layout',
      '.collection-flex-layout',
      '.description-section',
      '.collection-section',
      '.description-block',
      '.description-sections',
      '[class*="flex-layout"]',
      '[class*="image-column"]',
      '[class*="text-column"]'
    ];

    layoutSelectors.forEach(selector => {
      container.querySelectorAll(selector).forEach(el => {
        const parent = el.parentNode;
        Array.from(el.children).forEach(child => {
          parent.insertBefore(child.cloneNode(true), el);
        });
        el.remove();
      });
    });

    const allElements = container.querySelectorAll('*');
    allElements.forEach(el => {
      el.style.clear = '';
    });
  }

  /**
   * Unwrap divs and paragraphs that contain only images
   */
  unwrapImageWrappers(container) {
    let hasChanges = true;
    while (hasChanges) {
      hasChanges = false;
      const divs = container.querySelectorAll('div');
      for (let i = 0; i < divs.length; i++) {
        const div = divs[i];
        if (div.children.length === 1) {
          const child = div.children[0];
          if (child.tagName === 'IMG') {
            div.parentNode.insertBefore(child, div);
            div.remove();
            hasChanges = true;
            break;
          }
        }
      }
    }

    const paragraphs = container.querySelectorAll('p');
    paragraphs.forEach(p => {
      if (p.children.length === 1) {
        const child = p.children[0];
        if (child.tagName === 'IMG') {
          const parent = p.parentNode;
          parent.insertBefore(child, p);
          p.remove();
        }
      }
    });
  }

  /**
   * Process content and add float classes
   */
  processContent(container) {
    const children = Array.from(container.children);
    if (children.length === 0) return;

    let imageCount = 0;

    children.forEach((child) => {
      const tagName = child.tagName.toUpperCase();

      if (tagName === 'IMG') {
        this.applyFloatToImage(child, imageCount);
        imageCount++;
        return;
      }

      const imgInChild = child.querySelector('img');

      if (imgInChild) {
        this.applyFloatToImage(imgInChild, imageCount);
        imageCount++;

        const textClone = child.cloneNode(true);
        textClone.querySelector('img').remove();
        
        if (textClone.textContent.trim().length > 0) {
          child.parentNode.insertBefore(textClone, child);
        }
        return;
      }

      if (tagName === 'TABLE') {
        child.classList.add('full-width-table');
        return;
      }
    });

    // Process remaining images that might be nested
    const allImages = container.querySelectorAll('img');
    allImages.forEach(img => {
      if (!img.classList.contains('float-image')) {
        this.applyFloatToImage(img, imageCount);
        imageCount++;
      }
    });
  }

  /**
   * Apply float classes to image
   */
  applyFloatToImage(img, imageIndex) {
    const direction = (imageIndex % 2 === 0) ? 'left' : 'right';
    img.classList.add('float-image');
    if (this.options.alternateDirection) {
      img.classList.add('float-' + direction);
    } else {
      img.classList.add('float-left');
    }
  }

  /**
   * Setup responsive resize handler
   */
  setupResponsiveHandler() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.handleResize(), 100);
    });
  }

  /**
   * Handle window resize
   */
  handleResize() {
    const isMobile = window.innerWidth < this.options.mobileBreakpoint;

    this.containers.forEach(container => {
      container.querySelectorAll('.float-image').forEach(el => {
        el.classList.toggle('mobile-full', isMobile);
      });
    });
  }
}

function initDescriptionFormatter() {
  return new DescriptionFormatter().init();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDescriptionFormatter);
  } else {
    initDescriptionFormatter();
  }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = DescriptionFormatter;
}
